import React, { useMemo, useState, useEffect, useRef } from 'react';
import {
  getCountries,
  getCountryCallingCode,
  AsYouType,
  parsePhoneNumberFromString,
} from 'libphonenumber-js';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import {
  Command,
  CommandInput,
  CommandList,
  CommandEmpty,
  CommandGroup,
  CommandItem,
} from './ui/command';
import { ChevronDown, Check } from 'lucide-react';
import { cn } from '../lib/utils';

// ISO-3166 alpha-2 -> flag emoji (regional indicator symbols)
const isoToFlag = (iso) =>
  iso
    .toUpperCase()
    .replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));

const regionNames =
  typeof Intl !== 'undefined' && Intl.DisplayNames
    ? new Intl.DisplayNames(['en'], { type: 'region' })
    : null;

// Countries we serve are pinned to the top for fast selection.
const PINNED = ['US', 'AE'];

const buildCountryList = () => {
  const list = getCountries().map((iso) => ({
    iso,
    name: (regionNames && regionNames.of(iso)) || iso,
    dial: `+${getCountryCallingCode(iso)}`,
    flag: isoToFlag(iso),
  }));
  list.sort((a, b) => a.name.localeCompare(b.name));
  const pinned = PINNED.map((iso) => list.find((c) => c.iso === iso)).filter(Boolean);
  const rest = list.filter((c) => !PINNED.includes(c.iso));
  return [...pinned, ...rest];
};

/**
 * Shared, reusable international phone input.
 * - Searchable country dropdown (all ~250 countries) backed by libphonenumber-js (single source of truth).
 * - Emits an E.164 string via onChange(e164, { country, isValid }).
 */
export const PhoneInput = ({
  value = '',
  onChange,
  defaultCountry = 'US',
  disabled = false,
  error = false,
  id,
  placeholder = 'Phone number',
  'data-testid': testId = 'phone-input',
}) => {
  const countries = useMemo(buildCountryList, []);
  const [open, setOpen] = useState(false);
  const [country, setCountry] = useState(defaultCountry);
  const [national, setNational] = useState('');
  const initialised = useRef(false);

  // Initialise from an incoming E.164 value exactly once.
  useEffect(() => {
    if (initialised.current) return;
    initialised.current = true;
    if (value) {
      const parsed = parsePhoneNumberFromString(value);
      if (parsed) {
        setCountry(parsed.country || defaultCountry);
        setNational(parsed.formatNational());
      }
    }
  }, [value, defaultCountry]);

  const selected = countries.find((c) => c.iso === country) || countries[0];

  const emit = (iso, nationalValue) => {
    const parsed = parsePhoneNumberFromString(nationalValue || '', iso);
    onChange?.(parsed ? parsed.number : '', {
      country: iso,
      isValid: parsed ? parsed.isValid() : false,
    });
  };

  const handleNationalChange = (e) => {
    const cleaned = e.target.value.replace(/[^\d\s()+-]/g, '');
    const formatted = new AsYouType(country).input(cleaned);
    setNational(formatted);
    emit(country, formatted);
  };

  const handleCountrySelect = (iso) => {
    setCountry(iso);
    setOpen(false);
    emit(iso, national);
  };

  return (
    <div
      className={cn(
        'flex w-full rounded-lg border bg-white transition-colors',
        'focus-within:ring-2 focus-within:ring-blue-500 focus-within:border-blue-500',
        error ? 'border-red-400' : 'border-gray-300',
        disabled && 'opacity-60 pointer-events-none'
      )}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            data-testid={`${testId}-country-trigger`}
            aria-label={`Country code: ${selected.name} ${selected.dial}`}
            className="flex items-center gap-1.5 pl-3 pr-2 h-12 rounded-l-lg border-r border-gray-200 hover:bg-gray-50 focus:outline-none flex-shrink-0"
          >
            <span className="text-base leading-none">{selected.flag}</span>
            <span className="text-sm font-medium text-gray-700">{selected.dial}</span>
            <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="p-0 w-[300px]" align="start">
          <Command
            filter={(value, search) =>
              value.toLowerCase().includes(search.toLowerCase()) ? 1 : 0
            }
          >
            <CommandInput placeholder="Search country or code…" data-testid={`${testId}-country-search`} />
            <CommandList>
              <CommandEmpty>No country found.</CommandEmpty>
              <CommandGroup>
                {countries.map((c) => (
                  <CommandItem
                    key={c.iso}
                    value={`${c.name} ${c.dial} ${c.iso}`}
                    onSelect={() => handleCountrySelect(c.iso)}
                    data-testid={`${testId}-country-${c.iso}`}
                    className="cursor-pointer"
                  >
                    <span className="mr-2 text-base">{c.flag}</span>
                    <span className="flex-1 truncate">{c.name}</span>
                    <span className="ml-2 text-sm text-gray-400">{c.dial}</span>
                    {c.iso === country && <Check className="ml-2 w-4 h-4 text-blue-600" />}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      <input
        id={id}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        disabled={disabled}
        value={national}
        onChange={handleNationalChange}
        placeholder={placeholder}
        data-testid={testId}
        className="flex-1 min-w-0 h-12 px-3 text-base bg-transparent rounded-r-lg focus:outline-none"
      />
    </div>
  );
};

export default PhoneInput;
